import { useMemo, useRef, useState } from "react";
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import StarIcon from "@mui/icons-material/Star";
import StarOutlineIcon from "@mui/icons-material/StarOutline";
import { tokens } from "../theme";
import { useFormat } from "../i18n/format";
import {
  campaignCovers,
  localDayInZone,
  localMinutesOf,
} from "@shared/signage";
import { PLAYHEAD_SX, RAIL_WIDTH, RULER_HEIGHT } from "./SignageTimeline";
import {
  MIN_HOUR_WIDTH,
  hourLabelStep,
  useElementWidth,
} from "../utils/stripScale";
import type { SignageCampaign } from "@shared/types/signage";

/**
 * When each campaign may interrupt, one labelled lane apiece.
 *
 * ## One card, two rows
 *
 * A sibling of the content strip inside the SAME card, sharing its rail width
 * and its ruler geometry so the two line up column for column. As two bordered
 * cards they read as unrelated pictures of different things; as one card they
 * answer the question that actually connects them - which campaign is eligible
 * while WHICH playlist is on - by looking down a vertical line.
 *
 * (Folding the lanes into the content strip's BODY was the previous attempt
 * and went too far the other way: they lost their labels, and a bar with no
 * name is a coloured rectangle.)
 *
 * ## Why not a mark per break
 *
 * Ten breaks an hour draws a picket fence and answers nothing. What an
 * operator asks is which campaigns run WHEN, which is a question about
 * windows.
 *
 * ## Eligible, not airtime
 *
 * A bar means "may be chosen at a marker inside this window", not "plays
 * throughout". Which campaign takes a given break is decided by the rotation
 * bucket at that instant, and a `high` campaign is entered twice so it comes
 * round about twice as often - which is exactly what the star in the rail
 * toggles, and why that control belongs beside the day it applies to rather
 * than three pages away in a dialog.
 */

/**
 * The hour, at the SITE, not in the viewer's browser.
 *
 * Same rule the content strip works to and for the same reason: every instant
 * here is real UTC, so formatting with the browser's own offset silently
 * retimes the whole row for anyone managing a location in another zone.
 */
function hourLabel(ms: number, timeZone: string, locale: string): string {
  try {
    const parts = Object.fromEntries(
      new Intl.DateTimeFormat(locale, {
        timeZone,
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })
        .formatToParts(new Date(ms))
        .map((p) => [p.type, p.value])
    ) as Record<string, string>;
    const hour = parts.hour === "24" ? "00" : parts.hour;
    return `${hour}.${parts.minute}`;
  } catch {
    return "";
  }
}

/**
 * Tall enough that a bar can be shorter than its lane and still read.
 *
 * The gap either side of a bar is what lets the eye follow ONE campaign across
 * the day; butted rows read as a single block. `RULER_HEIGHT` comes from the
 * content strip rather than being declared again here - two copies of it is
 * two rows that stop lining up the moment one is adjusted.
 */
const LANE_HEIGHT = 54;
const BAR_INSET = 11;

export interface SignageCampaignLanesProps {
  campaigns: SignageCampaign[];
  /** Same origin and span as the content strip above. */
  geom: { originMs: number; hours: number; pctOf: (ms: number) => number };
  now: Date;
  timezone: string;
  /** Flip a campaign's priority. Absent = the star is shown but not offered. */
  onSetPriority?: (
    campaign: SignageCampaign,
    priority: "normal" | "high"
  ) => Promise<void>;
  /** Move with the content strip above. See `useLinkedScroll`. */
  scrollSync?: (el: HTMLElement | null) => void;
}

interface Span {
  fromMs: number;
  toMs: number;
}

/**
 * Sampled against `campaignCovers` rather than read off startTime/endTime.
 *
 * Eligibility is a day set, a date range and a window that can wrap midnight,
 * all decided in one place. Re-deriving the shape here would be the second
 * implementation the CMS and the panel then disagree about - the same reason
 * the strip above is drawn from the resolver.
 */
function spansFor(
  campaign: SignageCampaign,
  geom: SignageCampaignLanesProps["geom"],
  timezone: string
): Span[] {
  const STEP_MS = 15 * 60_000;
  const endMs = geom.originMs + geom.hours * 3_600_000;
  const out: Span[] = [];
  let open: number | null = null;
  for (let ms = geom.originMs; ms < endMs; ms += STEP_MS) {
    const at = new Date(ms);
    const local = localDayInZone(timezone, at);
    const covered = campaignCovers(
      campaign,
      local.ymd,
      local.day,
      localMinutesOf(timezone, at)
    );
    if (covered && open === null) open = ms;
    if (!covered && open !== null) {
      out.push({ fromMs: open, toMs: ms });
      open = null;
    }
  }
  if (open !== null) out.push({ fromMs: open, toMs: endMs });
  return out;
}

export default function SignageCampaignLanes({
  campaigns,
  geom,
  now,
  timezone,
  onSetPriority,
  scrollSync,
}: SignageCampaignLanesProps) {
  const { t } = useTranslation(["signage", "common"]);
  const { locale } = useFormat();
  const [busy, setBusy] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  /*
   * Measured independently of the strip above rather than passed down.
   *
   * The two are the same width by construction - same card, same rail - so
   * they arrive at the same step, and a row that measures itself keeps working
   * if it is ever used somewhere the other one is not.
   */
  const stripWidth = useElementWidth(scrollRef);
  const labelStep = hourLabelStep(stripWidth, geom.hours);

  const lanes = useMemo(
    () =>
      campaigns.map((campaign) => ({
        campaign,
        spans: spansFor(campaign, geom, timezone),
      })),
    [campaigns, geom, timezone]
  );

  if (lanes.length === 0) return null;

  const playheadPct = geom.pctOf(now.getTime());
  const minWidth = geom.hours * MIN_HOUR_WIDTH;

  const flip = async (c: SignageCampaign) => {
    if (!onSetPriority || busy) return;
    setBusy(c.id);
    try {
      await onSetPriority(c, c.priority === "high" ? "normal" : "high");
    } finally {
      setBusy(null);
    }
  };

  return (
    <Box
      data-testid="signage-campaign-lanes"
      sx={{ display: "flex", borderTop: `1px solid ${tokens.divider}` }}
    >
      {/* Labels, at the content rail's width so the two columns line up. */}
      <Box
        sx={{
          width: RAIL_WIDTH,
          flexShrink: 0,
          borderRight: `1px solid ${tokens.divider}`,
          bgcolor: tokens.brandTintSoft,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          gap={0.75}
          sx={{ height: RULER_HEIGHT, px: 2.5 }}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              bgcolor: tokens.brand,
            }}
          />
          <Typography
            variant="micro"
            sx={{
              fontWeight: "fontWeightBold",
              letterSpacing: "0.2px",
              color: tokens.brand,
            }}
          >
            {t("signageCampaignLanes.heading")}
          </Typography>
        </Stack>
        {lanes.map(({ campaign }) => (
          <Stack
            key={campaign.id}
            direction="row"
            alignItems="center"
            gap={0.75}
            sx={{ height: LANE_HEIGHT, px: 1.5 }}
          >
            <Tooltip
              title={
                campaign.priority === "high"
                  ? t("signageCampaignLanes.priorityTooltip")
                  : t("signageCampaignLanes.rotationTooltip")
              }
            >
              {/* A CONTROL, not an ornament. This is the priority, shown
                  against the day it applies to rather than three pages away
                  in a dialog. */}
              <span>
                <IconButton
                  size="small"
                  disabled={!onSetPriority || busy === campaign.id}
                  onClick={() => void flip(campaign)}
                  aria-label={t("signageCampaignLanes.priorityAria", {
                    name: campaign.name,
                  })}
                  aria-pressed={campaign.priority === "high"}
                >
                  {campaign.priority === "high" ? (
                    <StarIcon sx={{ fontSize: 17, color: tokens.brand }} />
                  ) : (
                    <StarOutlineIcon
                      sx={{ fontSize: 17, color: tokens.inkFaint }}
                    />
                  )}
                </IconButton>
              </span>
            </Tooltip>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="meta"
                noWrap
                sx={{ fontWeight: "fontWeightSemibold", color: tokens.ink }}
              >
                {campaign.name}
              </Typography>
              <Typography variant="micro" sx={{ color: tokens.inkMuted }}>
                {campaign.priority === "high"
                  ? t("common:labels.priority")
                  : t("signageCampaignLanes.scheduled")}
              </Typography>
            </Box>
          </Stack>
        ))}
      </Box>

      {/* Lanes, on the content strip's ruler. */}
      <Box
        ref={(el: HTMLDivElement | null) => {
          scrollRef.current = el;
          scrollSync?.(el);
        }}
        sx={{
          flex: 1,
          // ⛔ `minWidth: 0`, and the whole strip depends on it.
          //
          // A flex item defaults to `min-width: auto`, which means it REFUSES
          // to shrink below its content's intrinsic width. The inner box
          // carries `minWidth: hours * MIN_HOUR_WIDTH` so a day never squashes
          // to something unreadable - and without this line that floor is
          // inherited by the scroll container itself. It then grows past the
          // card instead of scrolling inside it, and the card's `overflow:
          // hidden` silently cuts the day off: no scrollbar, just a timeline
          // that stops at 16:00 and does not say why.
          //
          // Only visible when the viewport is narrower than the floor, which
          // is why it survived every desktop test and appeared on ctrl-+.
          minWidth: 0,
          overflowX: "auto",
          overflowY: "hidden",
          position: "relative",
        }}
      >
        <Box sx={{ width: "100%", minWidth, position: "relative" }}>
          {/*
            ITS OWN hour ruler, numbers and all.
            The first version drew gridlines only, on the reasoning that the
            strip above already prints the hours and repeating them is noise.
            That is wrong here for a mechanical reason: the two rows scroll
            INDEPENDENTLY, so the moment either is scrolled the columns no
            longer stand under the same hours, and a lane with no numbers of
            its own becomes unreadable rather than merely redundant.
          */}
          <Box
            sx={{
              height: RULER_HEIGHT,
              position: "sticky",
              top: 0,
              bgcolor: tokens.canvas,
              borderBottom: `1px solid ${tokens.divider}`,
            }}
          >
            {Array.from({ length: geom.hours }, (_, i) => i)
              .filter((i) => i % labelStep === 0)
              .map((i) => (
                <Typography
                  variant="micro"
                  key={i}
                  sx={{
                    position: "absolute",
                    left: `${(i / geom.hours) * 100}%`,
                    ml: "6px",
                    top: 8,
                    fontWeight: "fontWeightMedium",
                    letterSpacing: "0.2px",
                    color: tokens.inkFaint,
                  }}
                >
                  {hourLabel(geom.originMs + i * 3_600_000, timezone, locale)}
                </Typography>
              ))}
          </Box>
          {Array.from({ length: geom.hours }, (_, i) => i)
            .filter((i) => i % labelStep === 0)
            .map((i) => (
              <Box
                key={i}
                sx={{
                  position: "absolute",
                  left: `${(i / geom.hours) * 100}%`,
                  top: RULER_HEIGHT,
                  bottom: 0,
                  width: "1px",
                  bgcolor: tokens.divider,
                  opacity: 0.6,
                }}
              />
            ))}

          {lanes.map(({ campaign, spans }) => (
            <Box
              key={campaign.id}
              sx={{ height: LANE_HEIGHT, position: "relative" }}
            >
              {spans.map((span) => {
                const left = geom.pctOf(span.fromMs);
                const width = geom.pctOf(span.toMs) - left;
                return (
                  <Box
                    key={span.fromMs}
                    data-testid={`campaign-span-${campaign.id}`}
                    aria-label={t("signageCampaignLanes.spanAria", {
                      name: campaign.name,
                      state:
                        campaign.priority === "high"
                          ? t("common:labels.priority")
                          : t("signageCampaignLanes.scheduled"),
                    })}
                    sx={{
                      position: "absolute",
                      left: `${left}%`,
                      width: `${Math.max(0.2, width)}%`,
                      // AIR. The bar is shorter than its lane on purpose:
                      // butted rows read as one solid block and the eye cannot
                      // follow a single campaign across the day.
                      top: BAR_INSET,
                      height: LANE_HEIGHT - BAR_INSET * 2,
                      borderRadius: "6px",
                      // FLAT. A window is equally true at both ends, and a
                      // gradient says one end is weaker than the other.
                      bgcolor:
                        campaign.priority === "high"
                          ? tokens.brandTintSoft
                          : tokens.rowAlt,
                      borderLeft: `3px solid ${
                        campaign.priority === "high"
                          ? tokens.brand
                          : tokens.divider
                      }`,
                      display: "flex",
                      alignItems: "center",
                      px: 1,
                      overflow: "hidden",
                    }}
                  >
                    <Typography
                      variant="micro"
                      noWrap
                      sx={{
                        fontWeight: "fontWeightSemibold",
                        color:
                          campaign.priority === "high"
                            ? tokens.brand
                            : tokens.inkMuted,
                      }}
                    >
                      {campaign.name}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          ))}

          {playheadPct >= 0 && playheadPct <= 100 && (
            <Box
              data-testid="campaign-playhead"
              sx={{
                // The SAME marker as the strip above, from the same object.
                // One instant, one card, one shape - and the dimmed line it
                // used to be made the lower row look like a different, weaker
                // kind of "now".
                ...PLAYHEAD_SX,
                left: `${playheadPct}%`,
                top: RULER_HEIGHT,
                bottom: 0,
              }}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
}
